/*
 * App Router Component - Garbage Tracker Version
 *
 * Simplified routing for garbage truck tracking application
 * Handles user and admin authentication
 */

import React, { useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useAdminAuth } from '../../contexts/AuthContext';

// Components
import VehicleTracker from '../User/VehicleTracker';
import AdminPanel from '../Admin/AdminPanel';
import Login from '../Auth/Login';
import Register from '../Auth/Register';
import ForgotPassword from '../Auth/ForgotPassword';
import AdminLogin from '../Auth/AdminLogin';
import DriverApp from '../Driver/DriverApp';
import LoadingScreen from '../shared/LoadingScreen';

const AppRouter = ({ authMode, setAuthMode }) => {
  const { user, loading } = useAuth();
  const { admin, loading: adminLoading } = useAdminAuth();

  // Detect URL path and set appropriate authMode on component mount
  useEffect(() => {
    const path = window.location.pathname.replace(/\/+$/, '') || '/';

    if (path === '/register') {
      setAuthMode('register');
    } else if (path === '/forgot-password') {
      setAuthMode('forgot-password');
    } else if (path === '/admin') {
      setAuthMode('admin');
    } else if (path === '/driver') {
      setAuthMode('driver');
    } else if (path === '/' && !authMode) {
      setAuthMode('user');
    }
  }, []);

  // Update URL when authMode changes
  useEffect(() => {
    if (!user && !admin) {
      const currentPath = window.location.pathname;
      let newPath = '/';

      if (authMode === 'register') newPath = '/register';
      else if (authMode === 'forgot-password') newPath = '/forgot-password';
      else if (authMode === 'admin') newPath = '/admin';
      else if (authMode === 'driver') newPath = '/driver';

      if (currentPath !== newPath) {
        window.history.pushState({}, '', newPath + window.location.search);
      }
    }
  }, [authMode, user, admin]);

  // Show loading screen while authentication state is being determined
  if (loading || adminLoading) {
    return <LoadingScreen />;
  }

  // Driver app flow - manages its own auth (driverToken) inside DriverApp
  if (authMode === 'driver') {
    return <DriverApp />;
  }

  // Admin authentication flow
  if (authMode === 'admin') {
    if (!admin) {
      return <AdminLogin onBackToUser={() => setAuthMode('user')} />;
    }
    return <AdminPanel />;
  }

  // User authentication flow
  if (!user) {
    switch (authMode) {
      case 'register':
        return <Register onSwitchToLogin={() => setAuthMode('user')} />;
      case 'forgot-password':
        return <ForgotPassword onBackToLogin={() => setAuthMode('user')} />;
      default:
        return (
          <Login
            onSwitchToRegister={() => setAuthMode('register')}
            onSwitchToForgotPassword={() => setAuthMode('forgot-password')}
          />
        );
    }
  }

  // All authenticated users get the garbage truck tracker
  return <VehicleTracker />;
};

export default AppRouter;